import { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Add, Edit2, MagicStar, Trash } from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBSelect from "../../component/global/DBSelect";
import DBSwitch from "../../component/global/DBSwitch";
import DBTextArea from "../../component/global/DBTextArea";
import DBSpinner from "../../component/global/DBSpinner";
import DBConfirmModal from "../../component/global/DBConfirmModal";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SURVEY_SENDER_PROFILES,
    SURVEY_SUPPRESSIONS,
} from "../../helperData/ApiAddress";
import { SENDER_CHANNEL, senderChannelOptions } from "./constants";
import SenderConfigAIModal from "./SenderConfigAIModal";
import {
    buildProfileConfig,
    defaultAdvancedConfig,
    defaultProfileForm,
    formatProfileConfigSummary,
    profileToForm,
} from "./senderConfigUtils";

export default function SettingsPanel() {
    const toast = useContext(ToastContext);
    const [profiles, setProfiles] = useState([]);
    const [suppressions, setSuppressions] = useState([]);
    const [profileForm, setProfileForm] = useState(defaultProfileForm);
    const [profileError, setProfileError] = useState("");
    const [configError, setConfigError] = useState("");
    const [aiModalShow, setAiModalShow] = useState(false);
    const [suppressionForm, setSuppressionForm] = useState({
        value: "",
        channel: SENDER_CHANNEL.EMAIL,
        reason: "",
    });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [profileDeleteTarget, setProfileDeleteTarget] = useState(null);
    const editingProfile = Boolean(profileForm.id);

    const [getProfiles, getProfilesLoading] = useRequest(
        SURVEY_SENDER_PROFILES,
        "GET"
    );
    const [getSuppressions, getSuppressionsLoading] = useRequest(
        SURVEY_SUPPRESSIONS,
        "GET"
    );

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
    });

    const loadProfiles = () => {
        getProfiles({ page_size: 100 })
            .then((r) => setProfiles(r.results || r || []))
            .catch(() => setProfiles([]));
    };

    const loadSuppressions = () => {
        getSuppressions({ page_size: 100 })
            .then((r) => setSuppressions(r.results || r || []))
            .catch(() => setSuppressions([]));
    };

    useEffect(() => {
        loadProfiles();
        loadSuppressions();
    }, []);

    const handleChannelChange = (channel) => {
        setProfileForm((current) => ({
            ...current,
            channel,
            showAdvanced: false,
            advancedConfig: defaultAdvancedConfig(channel),
        }));
        setConfigError("");
    };

    const resetProfileForm = () => {
        setProfileForm(defaultProfileForm());
        setProfileError("");
        setConfigError("");
    };

    const handleSaveProfile = () => {
        if (!profileForm.name.trim()) {
            setProfileError(t("survey.sender.profile.name.required"));
            return;
        }

        let config;
        try {
            config = buildProfileConfig(profileForm);
        } catch {
            setConfigError(t("survey.sender.profile.config.invalid"));
            return;
        }

        const payload = {
            name: profileForm.name.trim(),
            channel: profileForm.channel,
            config,
            active: profileForm.active,
        };

        setProfileError("");
        setConfigError("");
        const request = editingProfile
            ? axios.put(
                  `${SURVEY_SENDER_PROFILES}${profileForm.id}/`,
                  payload,
                  { headers: authHeaders() }
              )
            : axios.post(SURVEY_SENDER_PROFILES, payload, {
                  headers: authHeaders(),
              });

        request
            .then(() => {
                toast(
                    t(
                        editingProfile
                            ? "survey.sender.profile.update.success"
                            : "survey.sender.profile.save.success"
                    ),
                    "success"
                );
                resetProfileForm();
                loadProfiles();
            })
            .catch(() =>
                toast(t("survey.sender.profile.save.error"), "danger")
            );
    };

    const handleEditProfile = (profile) => {
        setProfileForm(profileToForm(profile));
        setProfileError("");
        setConfigError("");
    };

    const handleDeleteProfile = () => {
        axios
            .delete(`${SURVEY_SENDER_PROFILES}${profileDeleteTarget.id}/`, {
                headers: authHeaders(),
            })
            .then(() => {
                if (profileForm.id === profileDeleteTarget.id) {
                    resetProfileForm();
                }
                setProfileDeleteTarget(null);
                toast(t("survey.sender.profile.delete.success"), "success");
                loadProfiles();
            })
            .catch(() => {
                setProfileDeleteTarget(null);
                toast(t("survey.sender.profile.delete.error"), "danger");
            });
    };

    const handleAiApply = ({ fromEmail, fromNumber, name }) => {
        setProfileForm((current) => ({
            ...current,
            ...(fromEmail !== undefined ? { fromEmail } : {}),
            ...(fromNumber !== undefined ? { fromNumber } : {}),
            ...(name !== undefined && !current.name.trim()
                ? { name }
                : {}),
            showAdvanced: false,
        }));
        toast(t("survey.sender.ai.applied"), "success");
    };

    const handleAddSuppression = () => {
        if (!suppressionForm.value.trim()) {
            toast(t("survey.suppression.value.required"), "danger");
            return;
        }
        axios
            .post(
                SURVEY_SUPPRESSIONS,
                {
                    value: suppressionForm.value.trim(),
                    channel: suppressionForm.channel,
                    reason: suppressionForm.reason,
                },
                { headers: authHeaders() }
            )
            .then(() => {
                setSuppressionForm({
                    value: "",
                    channel: SENDER_CHANNEL.EMAIL,
                    reason: "",
                });
                loadSuppressions();
            })
            .catch(() => toast(t("survey.save.error"), "danger"));
    };

    const handleDeleteSuppression = () => {
        axios
            .delete(`${SURVEY_SUPPRESSIONS}${deleteTarget.id}/`, {
                headers: authHeaders(),
            })
            .then(() => {
                setDeleteTarget(null);
                loadSuppressions();
            })
            .catch(() => {
                setDeleteTarget(null);
                toast(t("survey.save.error"), "danger");
            });
    };

    const showEmailField = profileForm.channel === SENDER_CHANNEL.EMAIL;
    const showSmsField = profileForm.channel === SENDER_CHANNEL.SMS;
    const showInAppNote = profileForm.channel === SENDER_CHANNEL.IN_APP;

    return (
        <div className="survey-settings-panel">
            <section className="survey-settings-section">
                <header className="survey-settings-section-header">
                    <div>
                        <p>{t("survey.sender.profile.description")}</p>
                    </div>
                </header>

                <div className="survey-form-grid">
                    <DBFieldBlock
                        title={t("survey.sender.profile.name")}
                        required
                        error={profileError}
                    >
                        <DBInput
                            size="large"
                            status={profileError && "error"}
                            value={profileForm.name}
                            placeholder={t(
                                "survey.sender.profile.name.placeholder"
                            )}
                            onChange={(e) => {
                                setProfileForm({
                                    ...profileForm,
                                    name: e.target.value,
                                });
                                setProfileError("");
                            }}
                        />
                    </DBFieldBlock>

                    <DBFieldBlock title={t("survey.sender.profile.channel")}>
                        <DBSelect
                            size="large"
                            disabled={editingProfile}
                            value={profileForm.channel}
                            onChange={handleChannelChange}
                            options={senderChannelOptions()}
                        />
                    </DBFieldBlock>

                    {showEmailField && !profileForm.showAdvanced && (
                        <DBFieldBlock
                            title={t("survey.sender.profile.from_email")}
                            span={2}
                        >
                            <div className="survey-config-field-row">
                                <DBInput
                                    size="large"
                                    value={profileForm.fromEmail}
                                    placeholder={t(
                                        "survey.sender.profile.from_email.placeholder"
                                    )}
                                    onChange={(e) =>
                                        setProfileForm({
                                            ...profileForm,
                                            fromEmail: e.target.value,
                                        })
                                    }
                                />
                                <button
                                    type="button"
                                    className="survey-ai-trigger"
                                    title={t("survey.sender.ai.title")}
                                    onClick={() => setAiModalShow(true)}
                                >
                                    <MagicStar size={18} />
                                </button>
                            </div>
                        </DBFieldBlock>
                    )}

                    {showSmsField && !profileForm.showAdvanced && (
                        <DBFieldBlock
                            title={t("survey.sender.profile.from_number")}
                            span={2}
                        >
                            <div className="survey-config-field-row">
                                <DBInput
                                    size="large"
                                    value={profileForm.fromNumber}
                                    placeholder={t(
                                        "survey.sender.profile.from_number.placeholder"
                                    )}
                                    onChange={(e) =>
                                        setProfileForm({
                                            ...profileForm,
                                            fromNumber: e.target.value,
                                        })
                                    }
                                />
                                <button
                                    type="button"
                                    className="survey-ai-trigger"
                                    title={t("survey.sender.ai.title")}
                                    onClick={() => setAiModalShow(true)}
                                >
                                    <MagicStar size={18} />
                                </button>
                            </div>
                            <p className="survey-field-hint">
                                {t("survey.sender.profile.from_number.hint")}
                            </p>
                        </DBFieldBlock>
                    )}

                    {showInAppNote && !profileForm.showAdvanced && (
                        <div className="survey-in-app-note" style={{ gridColumn: "span 2" }}>
                            <p>{t("survey.sender.profile.in_app.note")}</p>
                            <button
                                type="button"
                                className="survey-ai-trigger survey-ai-trigger-inline"
                                title={t("survey.sender.ai.title")}
                                onClick={() => setAiModalShow(true)}
                            >
                                <MagicStar size={16} />
                                <span>{t("survey.sender.ai.suggest_name")}</span>
                            </button>
                        </div>
                    )}

                    <div
                        className="survey-advanced-toggle"
                        style={{ gridColumn: "span 2" }}
                    >
                        <button
                            type="button"
                            onClick={() =>
                                setProfileForm((current) => {
                                    if (current.showAdvanced) {
                                        return {
                                            ...current,
                                            showAdvanced: false,
                                        };
                                    }

                                    let config;
                                    try {
                                        config = buildProfileConfig({
                                            ...current,
                                            showAdvanced: false,
                                        });
                                    } catch {
                                        config = {};
                                    }

                                    return {
                                        ...current,
                                        showAdvanced: true,
                                        advancedConfig: JSON.stringify(
                                            config,
                                            null,
                                            2
                                        ),
                                    };
                                })
                            }
                        >
                            {profileForm.showAdvanced
                                ? t("survey.sender.profile.config.simple")
                                : t("survey.sender.profile.config.advanced")}
                        </button>
                    </div>

                    {profileForm.showAdvanced && (
                        <DBFieldBlock
                            title={t("survey.sender.profile.config")}
                            span={2}
                            error={configError}
                        >
                            <div className="survey-config-field-row">
                                <DBTextArea
                                    rows={4}
                                    value={profileForm.advancedConfig}
                                    onChange={(e) => {
                                        setProfileForm({
                                            ...profileForm,
                                            advancedConfig: e.target.value,
                                        });
                                        setConfigError("");
                                    }}
                                />
                                <button
                                    type="button"
                                    className="survey-ai-trigger"
                                    title={t("survey.sender.ai.title")}
                                    onClick={() => setAiModalShow(true)}
                                >
                                    <MagicStar size={18} />
                                </button>
                            </div>
                        </DBFieldBlock>
                    )}

                    <DBFieldBlock title={t("survey.sender.profile.active")}>
                        <DBSwitch
                            checked={profileForm.active}
                            onChange={(value) =>
                                setProfileForm({
                                    ...profileForm,
                                    active: value,
                                })
                            }
                        />
                    </DBFieldBlock>
                </div>

                <div className="survey-settings-actions">
                    <DBButton
                        caption={
                            editingProfile ? (
                                t("survey.sender.profile.update")
                            ) : (
                                <>
                                    <Add size={18} />{" "}
                                    {t("survey.sender.profile.create")}
                                </>
                            )
                        }
                        onClick={handleSaveProfile}
                    />
                    {editingProfile && (
                        <DBButton
                            type="secondary"
                            outlined
                            caption={t("survey.sender.profile.cancel_edit")}
                            onClick={resetProfileForm}
                        />
                    )}
                </div>

                {getProfilesLoading ? (
                    <DBSpinner tip={t("loading")} />
                ) : profiles.length === 0 ? (
                    <div className="surveys-empty">
                        {t("survey.sender.profile.empty")}
                    </div>
                ) : (
                    <ul className="survey-settings-list">
                        {profiles.map((profile) => (
                            <li
                                key={profile.id}
                                className={
                                    profileForm.id === profile.id
                                        ? "is-editing"
                                        : ""
                                }
                            >
                                <div className="survey-settings-list-main">
                                    <strong>{profile.name}</strong>
                                    <span className="survey-settings-list-meta">
                                        {profile.channel_display}
                                        {!profile.active && (
                                            <span className="survey-status status-4">
                                                {t("survey.status.archived")}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <span className="survey-settings-list-config">
                                    {formatProfileConfigSummary(profile)}
                                </span>
                                <div className="survey-settings-list-actions">
                                    <button
                                        type="button"
                                        className="survey-settings-edit"
                                        title={t("survey.action.edit")}
                                        onClick={() => handleEditProfile(profile)}
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        className="survey-settings-delete"
                                        title={t("survey.sender.profile.delete")}
                                        onClick={() =>
                                            setProfileDeleteTarget(profile)
                                        }
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="survey-settings-section">
                <header className="survey-settings-section-header">
                    <div>
                        <h4>{t("survey.suppression.title")}</h4>
                        <p>{t("survey.suppression.description")}</p>
                    </div>
                </header>

                <div className="survey-form-grid">
                    <DBFieldBlock
                        title={t("survey.suppression.value")}
                        required
                    >
                        <DBInput
                            size="large"
                            value={suppressionForm.value}
                            placeholder={t(
                                "survey.suppression.value.placeholder"
                            )}
                            onChange={(e) =>
                                setSuppressionForm({
                                    ...suppressionForm,
                                    value: e.target.value,
                                })
                            }
                        />
                    </DBFieldBlock>
                    <DBFieldBlock title={t("survey.sender.profile.channel")}>
                        <DBSelect
                            size="large"
                            value={suppressionForm.channel}
                            onChange={(value) =>
                                setSuppressionForm({
                                    ...suppressionForm,
                                    channel: value,
                                })
                            }
                            options={senderChannelOptions()}
                        />
                    </DBFieldBlock>
                    <DBFieldBlock
                        title={t("survey.suppression.reason")}
                        span={2}
                    >
                        <DBInput
                            size="large"
                            value={suppressionForm.reason}
                            placeholder={t(
                                "survey.suppression.reason.placeholder"
                            )}
                            onChange={(e) =>
                                setSuppressionForm({
                                    ...suppressionForm,
                                    reason: e.target.value,
                                })
                            }
                        />
                    </DBFieldBlock>
                </div>

                <div className="survey-settings-actions">
                    <DBButton
                        caption={
                            <>
                                <Add size={18} /> {t("survey.suppression.add")}
                            </>
                        }
                        onClick={handleAddSuppression}
                    />
                </div>

                {getSuppressionsLoading ? (
                    <DBSpinner tip={t("loading")} />
                ) : suppressions.length === 0 ? (
                    <div className="surveys-empty">
                        {t("survey.suppression.empty")}
                    </div>
                ) : (
                    <ul className="survey-settings-list">
                        {suppressions.map((entry) => (
                            <li key={entry.id}>
                                <div className="survey-settings-list-main">
                                    <strong>{entry.value}</strong>
                                    <span className="survey-settings-list-meta">
                                        {entry.channel_display}
                                    </span>
                                </div>
                                {entry.reason && (
                                    <span className="survey-settings-list-reason">
                                        {entry.reason}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="survey-settings-delete"
                                    title={t("survey.action.archive")}
                                    onClick={() => setDeleteTarget(entry)}
                                >
                                    <Trash size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <SenderConfigAIModal
                show={aiModalShow}
                channel={profileForm.channel}
                onHide={() => setAiModalShow(false)}
                onApply={handleAiApply}
            />

            <DBConfirmModal
                show={!!profileDeleteTarget}
                onHide={() => setProfileDeleteTarget(null)}
                title={t("survey.sender.profile.delete.confirm.title")}
                description={t(
                    "survey.sender.profile.delete.confirm.description"
                )}
                btnTitle={t("survey.sender.profile.delete")}
                onConfirm={handleDeleteProfile}
            />

            <DBConfirmModal
                show={!!deleteTarget}
                onHide={() => setDeleteTarget(null)}
                title={t("survey.suppression.delete.confirm.title")}
                description={t("survey.suppression.delete.confirm.description")}
                btnTitle={t("survey.action.archive")}
                onConfirm={handleDeleteSuppression}
            />
        </div>
    );
}
